import { getUID } from '@redviking/argonaut-util/src/getUID';
import { accessor } from 'src/store';
import type { NodeSpec } from './NodeSpec';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import {
    type FlowNodeControl,
    defineControlOpts,
} from '../../controls/FlowNodeControl';

export const resetVariablesSpec: NodeSpec<NodeCfg<'resetVariables'>> = {
    nodeCategory: 'variable',
    kindLabel: 'Reset Variables',
    requiredSockets: [ 'trigger' ],
    outputs: () => ({ next: { socket: 'event', label: 'Next' } }),
    inputs: () => ({ trigger: { socket: 'event', label: 'Trigger' } }),
    cfg: () => ({ controlValues: { varIds: [] }, id: getUID(), kind: 'resetVariables', disabled: false }),
    controls: () => ({}),
    extraControls: nodeCfg => {
        const extraControls: Record<string, FlowNodeControl.WideOpts<unknown>> = {};
        extraControls.varIds = defineControlOpts({
            value: '',
            cfg: {
                kind: 'selectVar',
            },
            on: {
                'update:value': (value, opts) => {
                    const newNodeCfg = {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            varIds: [ ...new Set<string>([ ...nodeCfg.controlValues.varIds, value ]) ],
                        },
                    };
                    opts.flowEventEmitter.emit('updateNodeCfg', { newNodeCfg });
                },
            },
        });

        // add a control for each selected var
        for (const varId of nodeCfg.controlValues.varIds) {
            extraControls[`var:${varId}`] = defineControlOpts({
                cfg: {
                    kind: 'deletableSocket',
                    label: accessor.appletDesign.allAppletVariables[varId]?.name || 'Unknown Variable',
                },
                on: {
                    'update:value': () => {}, // no-op, this control does not use the update:value event
                    'delete': (_, opts) => {
                        const newNodeCfg = {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                varIds: nodeCfg.controlValues.varIds.filter(i => i !== varId),
                            },
                        };
                        opts.flowEventEmitter.emit('updateNodeCfg', { newNodeCfg });
                    },
                },
                value: null,
                enableWithConnectedSocket: true,
            });
        }
        
        return extraControls;
    },
    validator: (node) => {
        const errors: Record<string, string> = {};
        
        if (!node.controlValues.varIds.length) {
            errors.varIds = 'At least one variable is required';
        } else {
            // Validate each variable in the array
            const allAppletVariables = accessor.appletDesign.allAppletVariables;
            const invalidVars = node.controlValues.varIds.filter(varId => !allAppletVariables[varId]);
            for (const varId of invalidVars) {
                errors[`var:${varId}`] = 'Variable is not valid';
            }
        }
        
        return Object.keys(errors).length ? errors : true;
    },
};
